const VISITOR_COOKIE_NAME = 'beestmode_campaign_visitor';
const VISITOR_STORAGE_KEY = 'beestmode_campaign_visitor';
const ANALYTICS_EVENT_NAME = 'beestmode:campaign-analytics';
const ISRAEL_TIME_ZONE = 'Asia/Jerusalem';
const PIXEL_ENDPOINT = process.env.REACT_APP_CAMPAIGN_ANALYTICS_PIXEL_URL || '';

let visitorContext;
let knownLocationPromise;
const pendingPixels = new Set();

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;

  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
};

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
};

const persistVisitorId = (visitorId) => {
  if (typeof document === 'undefined') return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // Browsers may cap cookie lifetime, but this is intentionally set with the
  // furthest valid expiry and backed by localStorage for long-term continuity.
  document.cookie = `${encodeURIComponent(VISITOR_COOKIE_NAME)}=${encodeURIComponent(visitorId)}; Expires=Fri, 31 Dec 9999 23:59:59 GMT; Path=/; SameSite=Lax${secure}`;

  try {
    window.localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
  } catch (_error) {
    // Cookies still provide continuity when storage is disabled.
  }
};

const getVisitorContext = () => {
  if (visitorContext) return visitorContext;

  let visitorId = readCookie(VISITOR_COOKIE_NAME);

  if (!visitorId) {
    try {
      visitorId = window.localStorage.getItem(VISITOR_STORAGE_KEY);
    } catch (_error) {
      // A new ID will be used when both browser storage mechanisms are blocked.
    }
  }

  visitorContext = {
    visitorId: visitorId || createId(),
    isReturningVisitor: Boolean(visitorId),
  };

  persistVisitorId(visitorContext.visitorId);
  return visitorContext;
};

const getIsraelTimestamp = (date) => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: ISRAEL_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      timeZoneName: 'shortOffset',
    }).format(date);
  } catch (_error) {
    return date.toISOString();
  }
};

const getDeviceDetails = () => {
  if (typeof navigator === 'undefined') return {};

  return {
    platform: navigator.userAgentData?.platform || navigator.platform || 'unknown',
    userAgent: navigator.userAgent || 'unknown',
    language: navigator.language || 'unknown',
    screen: typeof window !== 'undefined'
      ? `${window.screen?.width || 0}x${window.screen?.height || 0}`
      : 'unknown',
    viewport: typeof window !== 'undefined'
      ? `${window.innerWidth || 0}x${window.innerHeight || 0}`
      : 'unknown',
    touchPoints: navigator.maxTouchPoints || 0,
  };
};

const getPreviouslyGrantedLocation = () => {
  if (knownLocationPromise) return knownLocationPromise;

  knownLocationPromise = (async () => {
    if (
      typeof navigator === 'undefined'
      || !navigator.permissions?.query
      || !navigator.geolocation?.getCurrentPosition
    ) {
      return null;
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });

      // Never call geolocation unless permission was granted before this visit.
      // This guarantees the campaign page cannot trigger a permission prompt.
      if (permission.state !== 'granted') return null;

      return await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => resolve({
            latitude: Number(coords.latitude.toFixed(5)),
            longitude: Number(coords.longitude.toFixed(5)),
            accuracyMeters: Math.round(coords.accuracy),
          }),
          () => resolve(null),
          { enableHighAccuracy: false, maximumAge: 300000, timeout: 2500 },
        );
      });
    } catch (_error) {
      return null;
    }
  })();

  return knownLocationPromise;
};

const firePixel = (payload) => {
  if (!PIXEL_ENDPOINT || typeof Image === 'undefined') return;

  try {
    const url = new URL(PIXEL_ENDPOINT, window.location.origin);

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      }
    });

    const pixel = new Image(1, 1);
    pendingPixels.add(pixel);
    const release = () => pendingPixels.delete(pixel);
    pixel.onload = release;
    pixel.onerror = release;
    pixel.src = url.toString();
  } catch (_error) {
    // Analytics must never interfere with the download experience.
  }
};

export const warmKnownLocation = () => {
  getPreviouslyGrantedLocation();
};

export const trackCampaignEvent = async (eventName, campaignDetails = {}) => {
  const occurredAt = new Date();
  const visitor = getVisitorContext();
  const location = await getPreviouslyGrantedLocation();
  const payload = {
    eventId: createId(),
    eventName,
    timestampUtc: occurredAt.toISOString(),
    timestampIsrael: getIsraelTimestamp(occurredAt),
    timestampTimeZone: ISRAEL_TIME_ZONE,
    visitorId: visitor.visitorId,
    isReturningVisitor: visitor.isReturningVisitor,
    path: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    device: getDeviceDetails(),
    location,
    ...campaignDetails,
  };

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ANALYTICS_EVENT_NAME, { detail: payload }));
  }

  firePixel(payload);
  return payload;
};

export { ANALYTICS_EVENT_NAME, VISITOR_COOKIE_NAME };
